import DataColumnCard, {
  DataColumnCardProps,
} from 'shared/components/RawMaterialMapColumnCard';
import { FlowNode, NodeLabels } from '../ProductFlowMap/productFlowMap.types';
import { getColorByValue } from '../ProductOverviewHelper';
import TransportationColumnCard, {
  TransportationColumnCardProps,
} from 'shared/components/TransportationMapColumnCard';
import PFColumnCard, {
  PFColumnCardProps,
} from 'shared/components/PFMapColumnCard';
import PDColumnCard, {
  PDColumnCardProps,
} from 'shared/components/PDColumnCard';
import React from 'react';
import { NodeType } from 'enums/ProductFlowMapEnums';

const getLabelCard = (
  columnNumber: number,
  label:
    | DataColumnCardProps
    | TransportationColumnCardProps
    | PFColumnCardProps
    | PDColumnCardProps
) => {
  let node;
  switch (columnNumber) {
    case 1:
      node = <DataColumnCard {...(label as DataColumnCardProps)} />;
      break;
    case 2:
      node = (
        <TransportationColumnCard
          {...(label as TransportationColumnCardProps)}
        />
      );
      break;
    case 3:
      node = <PFColumnCard {...(label as PFColumnCardProps)} />;
      break;
    case 4:
      node = <PDColumnCard {...(label as PDColumnCardProps)} />;
      break;
  }
  return node;
};

export const generateColumnNodes = (
  columnNumber: number,
  columnSize: number,
  containerHeight: number,
  labels?: NodeLabels[],
  col1Nodes?: NodeLabels[],
  isDownload?: boolean
) => {
  if (!labels?.length) return [];
  const filteredLabels = labels.filter(
    item => !(item.isSubNode && !item.isParentExpanded)
  );
  const filteredCol1Nodes = col1Nodes?.filter(
    item => !(item.isSubNode && !item.isParentExpanded)
  );
  if (
    filteredCol1Nodes?.length &&
    filteredCol1Nodes.length !== filteredLabels?.length
  )
    return [];
  const totalHeight = (
    filteredCol1Nodes?.length ? filteredCol1Nodes : filteredLabels
  ).reduce((sum, item) => sum + item.height + (item.isSubNode ? 0 : 15), 0);
  const startY = Math.max(50 + (containerHeight - totalHeight) / 2, 20);

  const nodes = filteredLabels.map((label, index) => {
    const addOnHeight =
      (label.isSubNode ? -15 : 0) +
      (columnNumber === 2 && filteredCol1Nodes?.length
        ? (filteredCol1Nodes[index].height - label.height) / 2
        : 0);

    const parentIndex = filteredLabels.findIndex(
      item => String(item.id) === label.parentId
    );
    const startIndex = parentIndex >= 0 ? parentIndex : 0;

    const subNodeHeight = filteredLabels
      .slice(startIndex, index)
      .reduce((sum, item) => sum + item.height, 0.5);
    const n = {
      id: `${label.id}`,
      columnNumber: columnNumber,
      type: NodeType.PRODUCT_FLOW_MAP_NODE,
      position: {
        x:
          label.isSubNode && !isDownload
            ? 0
            : (columnNumber - 3) * columnSize + columnSize / 2 - 50,
        y:
          label.isSubNode && !isDownload
            ? subNodeHeight
            : startY +
              (filteredCol1Nodes?.length ? filteredCol1Nodes : filteredLabels)
                .slice(0, index)
                .reduce(
                  (sum, item) => sum + item.height + (item.isSubNode ? 0 : 15),
                  0
                ) +
              addOnHeight,
      },
      data: {
        label: getLabelCard(columnNumber, label.label),
        hasSource: columnNumber !== 4,
        hasTarget: columnNumber !== 1,
      },
      emission: label.label.emission,
      isParent: !label.isSubNode,
      isSubNodesVisible: label.isSubNodesVisible,
      hasSubNodes: label.hasSubNodes,
      parentId: label.parentId && !isDownload ? String(label.parentId) : '',
    };
    return n;
  });
  return nodes;
};

export const generateEdgesForNodes = (columns: FlowNode[][]) => {
  const [column1, column2, column3, column4] = columns;
  if (!column1.length || !column2.length) return [];
  const filteredColumn1 = column1.filter(
    item => !(item.isParent && item.isSubNodesVisible)
  );
  const filteredColumn2 = column2.filter(
    item => !(item.isParent && item.isSubNodesVisible)
  );
  if (filteredColumn1?.length !== filteredColumn2?.length) return [];
  const col1ToCol2 = filteredColumn1.map((item, index) => {
    return {
      id: `${item.id}-${filteredColumn2[index].id}`,
      source: `${item.id}`,
      target: `${filteredColumn2[index].id}`,
      style: {
        stroke: getColorByValue(item.emission),
        strokeWidth: 1,
      },
    };
  });
  if (!column3.length) return [...col1ToCol2];

  const col2ToCol3 = column2
    ?.filter(item => item.isParent)
    .map(item => {
      return {
        id: `${item.id}-${column3[0].id}`,
        source: `${item.id}`,
        target: `${column3[0].id}`,
        style: {
          stroke: getColorByValue(item.emission),
          strokeWidth: 1,
        },
      };
    });
  if (!column4.length) return [...col1ToCol2, ...col2ToCol3];

  const col3ToCol4 = column4?.map(item => {
    return {
      id: `${column3[0].id}-${item.id}`,
      source: `${column3[0].id}`,
      target: `${item.id}`,
      style: {
        stroke: getColorByValue(item.emission),
        strokeWidth: 1,
      },
    };
  });

  return [...col1ToCol2, ...col2ToCol3, ...col3ToCol4];
};
